/** @jsx jsx */

import { ComponentProps, ReactNode, useEffect, useState } from 'react';

import { usePrevious } from '@zapier/common-hooks';
import { ProgressBar, Text } from '@zapier/design-system';
import { css, jsx } from '@emotion/core';

import { isSectionComplete } from 'app/cli-guide/utils';

import Sections from './Sections';

const Styles = {
  footer: css`
    padding: 30px 36px;
  `,
  root: (isMenuToggled: boolean) => css`
    padding-top: 16px;
    flex: 1;
    flex-direction: column;
    overflow: auto;
    width: 100%;
    display: ${isMenuToggled ? 'flex' : 'none'};
    @media screen and (min-width: 756px) {
      width: 340px;
      display: flex;
    }
  `,
  progress: css`
    min-height: 20px;
    margin: 10px 20px;
    padding-top: 7px;
  `,
};

type Props = Omit<ComponentProps<typeof Sections>, 'openSectons'> & {
  footer?: ReactNode;
  initialOpenSections: ComponentProps<typeof Sections>['openSections'];
  menuToggled: boolean;
  progress?: number;
  title?: string;
};

const defaultProps = {
  disabledSteps: [],
  footer: null,
  menuToggled: false,
  onSelectStep: () => {},
  initialOpenSections: {},
  progress: null,
  sections: [],
  stepsSeen: [],
  title: '',
};

function GuideSidebar(props: Props) {
  const {
    currentStep,
    disabledSteps,
    footer,
    initialOpenSections,
    menuToggled,
    onSelectStep,
    progress,
    sections,
    steps,
    stepsSeen,
    title,
  } = props;

  const stepsPrev = usePrevious(steps);
  const stepsSeenPrev = usePrevious(stepsSeen);

  const [openSections, setOpenSections] = useState(initialOpenSections);

  useEffect(() => {
    if (sections) {
      const openSectionsNext = { ...openSections };
      sections.forEach(section => {
        const isCurrentSectionDone = isSectionComplete(
          section,
          stepsPrev,
          stepsSeenPrev
        );
        const isNextSectionDone = isSectionComplete(section, steps, stepsSeen);
        if (!isCurrentSectionDone && isNextSectionDone) {
          openSectionsNext[section] = false;
        }
      });
      setOpenSections(openSectionsNext);
    }
  }, [openSections, sections, steps, stepsPrev, stepsSeen, stepsSeenPrev]);

  return (
    <div css={Styles.root(menuToggled)}>
      {title && (
        <Text margin="0 36px" type="Title">
          {title}
        </Text>
      )}
      {!!progress && (
        <div css={[Styles.progress]}>
          <ProgressBar max={100} value={progress} />
        </div>
      )}
      <Sections
        currentStep={currentStep}
        disabledSteps={disabledSteps}
        onSelectStep={onSelectStep}
        openSections={openSections}
        sections={sections}
        steps={steps}
        stepsSeen={stepsSeen}
      />
      {footer && <div css={Styles.footer}>{footer}</div>}
    </div>
  );
}

GuideSidebar.defaultProps = defaultProps;

export default GuideSidebar;
